import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from './LoginModal';
import RegisterModal from './RegisterModal';
import EditProfileModal from './EditProfileModal';
import UserStatsModal from './UserStatsModal';
import GameFeedbackWidget from './GameFeedbackWidget';
import { FALLBACK_PROFILE_IMAGE } from '../constants/profileDefaults';

interface MainHeaderProps {
  sticky?: boolean;
}

const MainHeader: React.FC<MainHeaderProps> = ({ sticky = true }) => {
  const navigate = useNavigate();
  const { user, login, logout, updateUser } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const hasProfileModalOpen = showEditProfileModal || showStatsModal;
  const isAdminUser = Boolean(
    user && (user.role === 'admin' || user.email?.toLowerCase() === 'rusu.george82@gmail.com')
  );

  const handleAuthSuccess = (token: string, authUser: any) => {
    login(token, authUser);
    setShowLoginModal(false);
    setShowRegisterModal(false);
  };

  const handleLogoutClick = () => {
    const confirmLogout = window.confirm('¿Quieres cerrar sesión?');
    if (confirmLogout) {
      logout();
    }
  };

  return (
    <>
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleAuthSuccess}
      />
      <RegisterModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onRegisterSuccess={handleAuthSuccess}
      />
      {user && (
        <EditProfileModal
          isOpen={showEditProfileModal}
          onClose={() => setShowEditProfileModal(false)}
          user={user}
          onProfileUpdated={updateUser}
        />
      )}
      {user && (
        <UserStatsModal
          isOpen={showStatsModal}
          onClose={() => setShowStatsModal(false)}
        />
      )}

      <header className={`${sticky ? 'sticky top-0' : ''} z-50 border-b border-white/10 bg-[#0a0a1a] px-3 py-4 md:px-6 flex items-center justify-between gap-2`}>
        <button onClick={() => navigate('/')} className="flex items-center hover:opacity-90 transition-opacity flex-shrink-0">
          <img
            src="/logos/main-gamer-logo.png"
            alt="Main Gamer"
            className="main-gamer-logo main-gamer-logo-static object-contain h-10 md:h-12 w-auto"
          />
        </button>

        <div className="flex items-center gap-3">
          {user ? (
            <div className="flex items-center gap-2">
              {isAdminUser && (
                <button
                  onClick={() => navigate('/admin/feedback')}
                  className="w-10 h-10 flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
                  title="Panel de administración"
                  aria-label="Panel de administración"
                >
                  <img
                    src="/logos/project-management.png"
                    alt="Panel admin"
                    className="w-9 h-9 object-contain"
                  />
                </button>
              )}
              <GameFeedbackWidget nickname={user.nickname} variant="header" />
              <button
                onClick={handleLogoutClick}
                className="w-10 h-10 flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
                title="Cerrar sesión"
                aria-label="Cerrar sesión"
              >
                <img src="/images/logout-icon.svg" alt="Cerrar sesión" className="w-10 h-10 object-contain block" />
              </button>

              <div className="relative group">
                <button className="flex items-center gap-3 rounded-xl px-2 py-1 hover:bg-white/5 transition-colors">
                  <div className="w-10 h-10 rounded-full bg-gaming-purple/30 border border-gaming-purple/50 overflow-hidden flex items-center justify-center">
                    <img src={user.fotoPerfil || FALLBACK_PROFILE_IMAGE} alt={user.nickname} className="w-[78%] h-[78%] object-contain" />
                  </div>
                  <div className="text-left">
                    <p className="text-white font-main text-sm leading-tight">{user.nickname}</p>
                    <p className="text-white/50 font-main text-xs leading-tight">Opciones de perfil</p>
                  </div>
                </button>

                {!hasProfileModalOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 rounded-xl border border-white/10 bg-black/90 backdrop-blur-md p-1 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-all z-50">
                    <button
                      onClick={() => {
                        setShowStatsModal(false);
                        setShowEditProfileModal(true);
                      }}
                      className="w-full text-left px-3 py-2 rounded-lg font-main text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors"
                    >
                      Editar perfil
                    </button>
                    <button
                      onClick={() => {
                        setShowEditProfileModal(false);
                        setShowStatsModal(true);
                      }}
                      className="w-full text-left px-3 py-2 rounded-lg font-main text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors"
                    >
                      Ver estadísticas
                    </button>
                    {isAdminUser && (
                      <button
                        onClick={() => navigate('/admin/feedback')}
                        className="w-full text-left px-3 py-2 rounded-lg font-main text-sm text-gaming-cyan hover:bg-white/10 hover:text-white transition-colors"
                      >
                        Panel admin
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
              <GameFeedbackWidget variant="header" />
              <button
                onClick={() => {
                  setShowRegisterModal(false);
                  setShowLoginModal(true);
                }}
                className="btn-neon-login text-[10px] md:text-sm px-2.5 py-1.5 md:px-4 md:py-2 flex-shrink-0"
              >
                INICIAR SESIÓN
              </button>
              <button
                onClick={() => {
                  setShowLoginModal(false);
                  setShowRegisterModal(true);
                }}
                className="btn-neon-register text-[10px] md:text-sm px-2.5 py-1.5 md:px-4 md:py-2 flex-shrink-0"
              >
                REGISTRARSE
              </button>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default MainHeader;
